/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import https from 'https';
import getConfig from '../../config';

declare let adobeIMS: AdobeIMS;
class CCStorageService {
	/**
	 * Retrieves the manifest data from CCStorage.
	 * @returns object - search datarequest response object
	 */
	fetchManifestData(manifestID) {
		return this._performCCStorageDataRequest(manifestID);
	}

	getValidAssetName(fileName, extension) {
		// file => xxxx.psdc
		// assetName => xxxx

		const request = (file, resolve, reject) => {
			this._performCCStorageNameCheck(file, this._getFileName(file))
				.then((data: any) => {
					if (data && data.statusCode === 200) {
						setTimeout(() => {
							request(this._generateNextValidAssetName(data.previousName, extension), resolve, reject);
						}, 1000);
					}
					else if (data && data.statusCode === 404) {
						resolve(data.res);
					}
					else {
						const error = new Error('_performCCStorageMonitor error');
						reject(error);
					}
				})
				.catch(error => reject(error));
		};

		return new Promise((resolve, reject) => {
			request(this._generateNextValidAssetName(fileName, extension), resolve, reject);
		});
	}

	copyAsset(assetUrn: string, targetPath: string) {
		return new Promise((resolve, reject) => {
			this._performCCStorageCopy(assetUrn, targetPath)
				.then((data: any) => {
					if (data && data.href) {
						this.monitor(data.href, assetUrn)
							.then(result => resolve(result))
							.catch((error) => {
								reject(error);
							});
					}
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	monitor(url, assetUrn) {
		const request = (url: string, resolve, reject) => {
			this._performCCStorageMonitor(url)
				.then((data: any) => {
					if (data.statusCode === 202) {
						setTimeout(() => {
							request(data.res.href, resolve, reject);
						}, 1000);
					}
					else if (data.statusCode === 200) {
						resolve(data.res);
					}
					else {
						const error = new Error('_performCCStorageMonitor error');
						reject(error);
					}
				})
				.catch(error => reject(error));
		};

		return new Promise((resolve, reject) => {
			request(url, resolve, reject);
		});
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: CCStorageService;

	constructor() {
		if (!CCStorageService.instance) {
			CCStorageService.instance = this;
		}
		return CCStorageService.instance;
	}

	_getFileName(file: string) {
		const fileArr = file.split('.');
		return fileArr.slice(0, fileArr.length - 1).join('.');
	}

	_generateNextValidAssetName(fileName: string, extension: string) {
		const nameArr = fileName.split(' ');
		let newFile = '';
		const last = nameArr[nameArr.length - 1];

		if (last !== 'copy' && isNaN(+last.substring(1, last.length - 1))) {
			// file has not been appended copy, or copy (1), etc
			newFile = `${fileName} copy.${extension}`;
		}
		else {
			if (last === 'copy') {
				newFile = `${fileName} (1).${extension}`;
			}
			else {
				const num = +last.substring(1, last.length - 1) + 1;
				newFile = `${nameArr.slice(0, nameArr.length - 1).join(' ')} (${num}).${extension}`;
			}
		}

		return newFile;
	}

	_performCCStorageNameCheck(file, fileName) {
		return new Promise((resolve, reject) => {
			const request = https.request(
				{
					hostname: getConfig('SERVICES').ccstorage.hostname,
					path: `/cloud-content/${file}`,
					method: 'HEAD',
					headers: {
						'Content-Type': 'application/json',
						Accept: '*/*',
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode === 404) {
						resolve({
							statusCode: 404,
							res: file,
						});
					}
					if (res.statusCode && res.statusCode >= 300) {
						// On error, it should show the error status code
						window.cchome.util.logger.error('CC Storage Service', 'Problem with CCStorage Monitor', res);
						const ccStorageServiceError = new Error('_performCCStorageMonitor error');
						resolve(ccStorageServiceError);
					}
					else if (res.statusCode && res.statusCode === 200) {
						resolve({
							previousName: fileName,
							statusCode: 200,
						});
					}
				},
			);

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error('CC Storage Service', 'Problem with CCStorage Monitor', err.toString());
				const ccStorageServiceError = new Error('_performCCStorageMonitor error');
				resolve(ccStorageServiceError);
			});
			request.end();
		});
	}

	/**
	 * Request monitor Ops.
	 * @param {assetID} assetId id
	 * @param {path} path id
	 * @returns {Object}
	 */
	_performCCStorageMonitor(path: string) {
		return new Promise((resolve) => {
			const request = https.request(
				{
					hostname: getConfig('SERVICES').ccstorage.hostname,
					path,
					method: 'GET',
					headers: {
						'Content-Type': 'application/vnd.adobe.asset-operation+json',
						Accept: '*/*',
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						// On error, it should show the error status code
						window.cchome.util.logger.error('CC Storage Service', 'Problem with CCStorage Monitor', res);
						const ccStorageServiceError = new Error('_performCCStorageMonitor error');
						resolve(ccStorageServiceError);
					}
					else if (res.statusCode && res.statusCode === 202) {
						resolve({
							statusCode: 202,
							res: {
								href: path,
							},
						});
					}
					else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', (chunk) => {
							data += chunk;
						});
						res.on('end', () => {
							// Success!
							try {
								const parsed = JSON.parse(data.substring(data.indexOf('{')));
								resolve({
									statusCode: 200,
									res: parsed,
								});
							}
							catch (e) {
								resolve(data);
							}
						});
					}
				},
			);

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error('CC Storage Service', 'Problem with CCStorage Monitor', err.toString());
				const ccStorageServiceError = new Error('_performCCStorageMonitor error');
				resolve(ccStorageServiceError);
			});
			request.end();
		});
	}

	/**
	 * Request copy to Your Work.
	 * @param {assetID} assetId id
	 * @param {path} path id
	 * @returns {Object}
	 */
	_performCCStorageCopy(assetId: string, path: string) {
		return new Promise((resolve) => {
			const request = https.request(
				{
					hostname: getConfig('SERVICES').ccstorage.hostname,
					path: '/ops?invocation_mode=ASYNC',
					method: 'POST',
					headers: {
						'Content-Type': 'application/vnd.adobe.asset-operation+json',
						Accept: '*/*',
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						// On error, it should show the error status code
						window.cchome.util.logger.error('CC Storage Service', 'Problem with CCStorage Copy', res);
						const ccStorageServiceError = new Error('_performCCStorageCopy error');
						resolve(ccStorageServiceError);
					}
					else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', chunk => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								resolve(JSON.parse(data));
							}
							catch (e) {
								resolve(data);
							}
						});
					}
				},
			);

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error('CC Storage Service', 'Problem with CCStorage Copy', err.toString());
				const ccStorageServiceError = new Error('_performCCStorageCopy error');
				resolve(ccStorageServiceError);
			});
			request.write(
				JSON.stringify({
					op: 'copy',
					source: {asset_id: assetId},
					target: {path},
				}),
			);
			request.end();
		});
	}

	/**
	 * Retrieves Manifest ID from CCStorage.
	 * @param {assetID} asset id
	 * @returns {Object}
	 */
	_performCCStorageDataRequest(manifestID: string) {
		return new Promise((resolve) => {
			const request = https.request(
				{
					hostname: getConfig('CCSTORAGE_URI'),
					path: `/id/${manifestID}`,
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						// On error, it should show the error status code
						window.cchome.util.logger.error(
							'CC Storage Service',
							'Problem with CCStorage Data Service request',
							res,
						);
						const ccStorageServiceError = new Error('_performCCStorageDataRequest error');
						resolve(ccStorageServiceError);
					}
					else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', chunk => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								resolve(JSON.parse(data));
							}
							catch (e) {
								resolve(data);
							}
						});
					}
				},
			);

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error(
					'CC Storage Service',
					'Problem with CCStorage Data Service request',
					err.toString(),
				);
				const ccStorageServiceError = new Error('_performCCStorageDataRequest error');
				resolve(ccStorageServiceError);
			});
			request.end();
		});
	}
}

export default CCStorageService;
